import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { faPowerOff, faCopy, faExternalLinkAlt, faGift, faGlobe, faDollarSign, faPlay, faFlag } from '@fortawesome/pro-light-svg-icons';
import { spotlights } from '@shopmy/universal';

import { SkeletonTable, SkeletonLines } from '../SpotlightsSkeleton';
import SpotlightsSubTable from '../SpotlightsSubTable';
import TrialGrantModal from '../TrialGrantModal';
import BrandFeatureFlagsModal from '../BrandFeatureFlagsModal';
import BrandFeatureFlagsBulkModal from '../BrandFeatureFlagsBulkModal';
import SelectAllBox from '../SelectAllBox';
import LifecycleBar from '../LifecycleBar';
import useCollapse from '../useCollapse';
import { SpotlightsActionMenu, SpotlightsSortMenu, SpotlightsFilterMenu, FilterField, FilterOption } from '../SpotlightsMenus';
import {
  getBrands,
  getBrandDetail,
  setSpotlightEnabled,
  setAllowNonDtcDestinationLinks,
  setAllowBelowMinDailyBudget,
  unpauseBillingPausedSpotlights
} from '../../../api/spotlights';
import { fmtMoney, fmtInt, fmtRoas, fmtPct, getThresholds } from '../../../helpers/spotlights_dashboard_helpers';
import { getSmartImage } from '../../../helpers/helpers';

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'needsAction', label: 'Needs action' },
  { key: 'hasRecommendations', label: 'Has recommendations' },
  { key: 'pauseRec', label: 'Pause rec.' },
  { key: 'lowRoas', label: 'Low ROAS' },
  { key: 'noSpend', label: 'No spend' },
  { key: 'billingPaused', label: 'Billing paused' },
  { key: 'pausedAll', label: 'Paused by brand' },
  { key: 'noPaymentMethod', label: 'No payment method' }
];

const COLUMNS = [
  { key: 'name', label: 'Brand', sortable: true },
  { key: 'live', label: 'Live', sortable: true, num: true },
  { key: 'spend', label: 'Spend', sortable: true, num: true },
  { key: 'orders', label: 'Orders', sortable: true, num: true },
  { key: 'roasSM', label: 'SM ROAS', sortable: true, num: true },
  { key: 'roas', label: 'AN ROAS', sortable: true, num: true },
  { key: 'trialStatus', label: 'Trial', sortable: true },
  { key: 'goLive', label: 'Go Live', sortable: true },
  { key: 'payment', label: 'Payment', sortable: false },
  { key: 'audienceExclusions', label: 'Audience Excl.', sortable: false },
  { key: 'utm', label: 'UTM', sortable: false },
  { key: 'flags', label: 'Flags', sortable: false }
];

const SORT_OPTIONS = COLUMNS.filter(c => c.sortable).map(c => ({ key: c.key, label: c.label }));
const { SPOTLIGHT_BRAND_FEATURE_FLAGS } = spotlights;

const SpotlightsBrands = ({ navParams = {} }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  // Seed the filter from a cross-tab navigation (e.g. an Overview "Needs attention" card).
  const [filter, setFilter] = useState(navParams.filter || '');
  const [mine, setMine] = useState(!!navParams.mine);
  const [sortBy, setSortBy] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [trialOpen, setTrialOpen] = useState(false);
  const [trialPreset, setTrialPreset] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [flagsBrand, setFlagsBrand] = useState(null);
  const [bulkFlagsBrandIds, setBulkFlagsBrandIds] = useState(null);

  const openTrial = (preset = null) => {
    setTrialPreset(preset);
    setTrialOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getBrands({ query: search, filter, mine: mine ? 'true' : '', sortBy, sortDir, page });
      setRows(r.brands || []);
      setTotal(r.total || 0);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load brands.');
    } finally {
      setLoading(false);
    }
  }, [search, filter, mine, sortBy, sortDir, page]);

  useEffect(() => {
    load();
  }, [load]);

  const onSort = key => {
    if (!COLUMNS.find(c => c.key === key)?.sortable) return;
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(0);
  };

  const submitSearch = e => {
    e.preventDefault();
    setPage(0);
    setSearch(query);
  };

  const pages = Math.ceil(total / 50);

  const onBulkFlagsDone = () => {
    setSelected(new Set());
    load();
  };

  const toggleOne = id =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const visibleIds = rows.map(b => b.Brand_id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someSelected = visibleIds.some(id => selected.has(id));
  const toggleAll = () =>
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });

  return (
    <div className='spotlights-brands'>
      <div className='sl-controls'>
        <form onSubmit={submitSearch}>
          <input className='sl-search' type='text' placeholder='Search brands…' value={query} onChange={e => setQuery(e.target.value)} />
        </form>
        <SpotlightsFilterMenu count={(filter ? 1 : 0) + (mine ? 1 : 0)} onClear={() => { setFilter(''); setMine(false); setPage(0); }}>
          <FilterField label='Show'>
            {FILTERS.map(f => (
              <FilterOption key={f.key} label={f.label} selected={filter === f.key} onClick={() => { setFilter(f.key); setPage(0); }} />
            ))}
          </FilterField>
          <FilterField>
            <label>
              <input type='checkbox' checked={mine} onChange={e => { setMine(e.target.checked); setPage(0); }} /> My brands
            </label>
          </FilterField>
        </SpotlightsFilterMenu>
        <SpotlightsSortMenu options={SORT_OPTIONS} value={sortBy} dir={sortDir} onChange={(key, d) => { setSortBy(key); setSortDir(d); setPage(0); }} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ color: '#767676', fontSize: 13 }}>{fmtInt(total)} brands</span>
          <button className='sl-btn secondary' onClick={() => setBulkFlagsBrandIds([...selected])}>
            Set feature flags
          </button>
          <button className='sl-btn' onClick={() => openTrial(null)}>
            Grant trial
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className='sl-bulk-bar'>
          <span>{selected.size} selected</span>
          <button className='sl-btn small' onClick={() => setBulkFlagsBrandIds([...selected])}>
            Set feature flags
          </button>
          <button className='sl-btn secondary small' onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {trialOpen && <TrialGrantModal presetBrand={trialPreset} onClose={() => setTrialOpen(false)} onDone={load} />}
      {flagsBrand && <BrandFeatureFlagsModal brand={flagsBrand} onClose={() => setFlagsBrand(null)} onDone={load} />}
      {bulkFlagsBrandIds && (
        <BrandFeatureFlagsBulkModal presetBrandIds={bulkFlagsBrandIds} onClose={() => setBulkFlagsBrandIds(null)} onDone={onBulkFlagsDone} />
      )}

      {error ? (
        <div className='sl-error'>{error}</div>
      ) : loading && !rows.length ? (
        <SkeletonTable rows={8} cols={COLUMNS.length + 3} />
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          <table className='sl-table'>
            <thead>
              <tr>
                <th className='no-sort sl-check-col'>
                  <SelectAllBox
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={toggleAll}
                    disabled={!visibleIds.length}
                  />
                </th>
                <th className='no-sort' style={{ width: 24 }} />
                {COLUMNS.map(c => (
                  <th key={c.key} className={cn({ 'no-sort': !c.sortable, num: c.num })} onClick={() => onSort(c.key)}>
                    {c.label} {sortBy === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th className='no-sort' style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(b => (
                <BrandRow
                  key={b.Brand_id}
                  brand={b}
                  expanded={expanded === b.Brand_id}
                  onToggle={() => setExpanded(expanded === b.Brand_id ? null : b.Brand_id)}
                  onChanged={load}
                  onGrantTrial={openTrial}
                  selected={selected.has(b.Brand_id)}
                  onSelect={() => toggleOne(b.Brand_id)}
                  onEditFlags={setFlagsBrand}
                />
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={COLUMNS.length + 3} className='sl-empty'>
                    No brands match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {pages > 1 && (
            <div className='sl-controls' style={{ justifyContent: 'center', marginTop: 16 }}>
              <button className='sl-btn secondary small' disabled={page === 0} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              <span style={{ fontSize: 13 }}>
                Page {page + 1} of {pages}
              </span>
              <button className='sl-btn secondary small' disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BrandRow = ({ brand, expanded, onToggle, onChanged, onGrantTrial, selected, onSelect, onEditFlags }) => {
  const [saving, setSaving] = useState(false);
  const { mounted, closing } = useCollapse(expanded);

  const toggleEnabled = async () => {
    setSaving(true);
    try {
      await setSpotlightEnabled(brand.Brand_id, !brand.isSpotlightEnabled);
      cogoToast.success(`Spotlights ${brand.isSpotlightEnabled ? 'disabled' : 'enabled'} for ${brand.brandName}`);
      onChanged();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAllowNonDtc = async () => {
    setSaving(true);
    try {
      await setAllowNonDtcDestinationLinks(brand.Brand_id, !brand.allowNonDtcDestinationLinks);
      cogoToast.success(`Non-DTC destination links ${brand.allowNonDtcDestinationLinks ? 'disabled' : 'enabled'} for ${brand.brandName}`);
      onChanged();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAllowBelowMinBudget = async () => {
    setSaving(true);
    try {
      await setAllowBelowMinDailyBudget(brand.Brand_id, !brand.allowBelowMinDailyBudget);
      cogoToast.success(`Below-minimum daily budget ${brand.allowBelowMinDailyBudget ? 'disabled' : 'enabled'} for ${brand.brandName}`);
      onChanged();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const unpauseBilling = async () => {
    const confirmed = window.confirm(
      `Unpause billing-paused spotlights for ${brand.brandName}? They will resume spending even though the brand still has unpaid ad-spend invoice(s).`
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      const r = await unpauseBillingPausedSpotlights(brand.Brand_id);
      cogoToast.success(`Resumed ${r.resumedCount} spotlight(s) for ${brand.brandName}`);
      onChanged();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to unpause.');
    } finally {
      setSaving(false);
    }
  };

  const actions = [
    { label: 'Grant trial', icon: faGift, onClick: () => onGrantTrial && onGrantTrial(brand) },
    { label: 'Feature flags', icon: faFlag, onClick: () => onEditFlags(brand) },
    { label: brand.isSpotlightEnabled ? 'Disable spotlights' : 'Enable spotlights', icon: faPowerOff, disabled: saving, onClick: toggleEnabled },
    brand.billingPaused && { label: 'Unpause billing-paused spotlights', icon: faPlay, disabled: saving, onClick: unpauseBilling },
    {
      label: brand.allowNonDtcDestinationLinks ? 'Disallow non-DTC links' : 'Allow non-DTC links',
      icon: faGlobe,
      disabled: saving,
      onClick: toggleAllowNonDtc
    },
    {
      label: brand.allowBelowMinDailyBudget ? 'Enforce $150 min budget' : 'Allow below-min budget',
      icon: faDollarSign,
      disabled: saving,
      onClick: toggleAllowBelowMinBudget
    },
    {
      label: 'Copy Brand ID',
      icon: faCopy,
      onClick: () => {
        navigator.clipboard?.writeText(String(brand.Brand_id));
        cogoToast.success('Copied Brand ID');
      }
    }
  ];

  return (
    <>
      <tr className={cn('sl-row-clickable', { 'sl-row-selected': selected })} onClick={onToggle}>
        <td className='sl-check-col' onClick={e => e.stopPropagation()}>
          <input type='checkbox' checked={selected} onChange={onSelect} />
        </td>
        <td>
          <span className={cn('sl-caret', { open: expanded })}>▸</span>
        </td>
        <td>
          <div className='sl-brand-cell'>
            {brand.brandLogo && <img src={getSmartImage(brand.brandLogo)} alt='' />}
            {brand.brandName}
            {brand.spotlightCount === 0 && <span className='sl-pill amber'>No spotlights</span>}
          </div>
        </td>
        <td className='num'>{fmtInt(brand.liveCount)}</td>
        <td className='num'>{fmtMoney(brand.spend)}</td>
        <td className='num'>{fmtInt(brand.orders)}</td>
        <td className='num'>{fmtRoas(brand.roasSM)}</td>
        <td className='num'>{fmtRoas(brand.roasAN)}</td>
        <td>
          {brand.trialStatus === 'evergreen' ? (
            <span className='sl-pill green'>Evergreen</span>
          ) : brand.trialStatus === 'paid' ? (
            <span className='sl-pill green'>Paid</span>
          ) : brand.trialStatus === 'expired' ? (
            <span className='sl-pill red'>Expired</span>
          ) : brand.trialStatus === 'trial' ? (
            <span title='Trial ends'>{moment(brand.trialEndsAt).format('MMM D, YYYY')}</span>
          ) : (
            '—'
          )}
        </td>
        <td>{brand.goLiveAt ? moment(brand.goLiveAt).format('MMM D, YYYY') : '—'}</td>
        <td>
          <div className='sl-pill-row'>
            {brand.hasPaymentMethod ? (
              <span className='sl-pill green'>On file</span>
            ) : (
              !brand.skipPaymentMethod && <span className='sl-pill red'>Missing</span>
            )}
            {brand.netPaymentTerms > 0 && <span className='sl-pill blue'>Net {brand.netPaymentTerms}d</span>}
            {brand.skipPaymentMethod && <span className='sl-pill blue'>Manual pay</span>}
            {brand.platformFeePercentage != null && <span>{brand.platformFeePercentage}% fee</span>}
          </div>
        </td>
        <td title={brand.hasAudienceExclusions ? 'Audience exclusions configured' : 'No audience exclusions'}>{brand.hasAudienceExclusions ? 'Y' : 'N'}</td>
        <td title={brand.hasUtmParams ? 'UTM parameters configured' : 'No UTM parameters'}>{brand.hasUtmParams ? 'Y' : 'N'}</td>
        <td>
          <Flags brand={brand} />
        </td>
        <td onClick={e => e.stopPropagation()}>
          <SpotlightsActionMenu options={actions} />
        </td>
      </tr>
      {mounted && (
        <tr>
          <td colSpan={COLUMNS.length + 3} className={cn('sl-expanded', { closing })}>
            <BrandDetail brand={brand} />
          </td>
        </tr>
      )}
    </>
  );
};

const Flags = ({ brand }) => {
  const thresholds = getThresholds();
  const rejectionAlert = (brand.rejectionRate || 0) > thresholds.rejectionRate;
  const anlAlert = (brand.acceptedNotLaunchedRate || 0) > thresholds.acceptedNotLaunchedRate;
  const enabledFlags = SPOTLIGHT_BRAND_FEATURE_FLAGS.filter(flag => brand.spotlightFeatureFlags?.[flag.key]);
  return (
    <div className='sl-pill-row'>
      {brand.pauseRecCount > 0 && <span className='sl-pill red'>↓ {brand.pauseRecCount}</span>}
      {brand.billingPaused && <span className='sl-pill amber'>Billing paused</span>}
      {brand.pausedAllAt && (
        <span className='sl-pill amber' title={`Paused all Spotlights ${new Date(brand.pausedAllAt).toLocaleDateString()}`}>
          Paused by brand
        </span>
      )}
      {rejectionAlert && <span className='sl-pill red' title={`Rejection rate ${fmtPct(brand.rejectionRate)}`}>High rejection</span>}
      {anlAlert && <span className='sl-pill red' title={`Accepted-not-launched ${fmtPct(brand.acceptedNotLaunchedRate)}`}>Stalled</span>}
      {!brand.isSpotlightEnabled && <span className='sl-pill'>Not enabled</span>}
      {enabledFlags.length > 0 && (
        <span className='sl-pill blue' title={enabledFlags.map(flag => flag.label).join(', ')}>
          {enabledFlags[0].label}
          {enabledFlags.length > 1 ? ` +${enabledFlags.length - 1}` : ''}
        </span>
      )}
    </div>
  );
};

const BrandDetail = ({ brand }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const thresholds = getThresholds();

  useEffect(() => {
    let active = true;
    getBrandDetail(brand.Brand_id)
      .then(d => active && setDetail(d))
      .catch(e => {
        if (!active) return;
        setDetail(false);
        window.ALERT.error(typeof e === 'string' ? e : 'Failed to load brand detail.');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [brand.Brand_id]);

  if (loading) return <SkeletonLines count={5} />;
  if (!detail) return <div className='sl-error'>Could not load detail.</div>;

  const f = detail.funnel;
  const acceptedNotLaunchedRate = f.accepted > 0 ? f.acceptedNotLaunched / f.accepted : 0;
  const rejectionAlert = f.rejectionRate > thresholds.rejectionRate;
  const anlAlert = acceptedNotLaunchedRate > thresholds.acceptedNotLaunchedRate;

  return (
    <div>
      {/* Lifecycle — segmented bar colored like the Overview donut */}
      <div style={{ marginBottom: 14 }}>
        <div className='sl-card-label'>Lifecycle</div>
        <div style={{ marginTop: 8 }}>
          <LifecycleBar lifecycle={detail.lifecycle} />
        </div>
      </div>

      {/* Authorization funnel */}
      <div style={{ marginBottom: 14 }}>
        <div className='sl-card-label'>Authorization funnel</div>
        <div className='sl-funnel' style={{ marginTop: 6 }}>
          <FunnelItem v={fmtInt(f.sent)} l='Sent' />
          <FunnelItem v={fmtInt(f.accepted)} l='Accepted' />
          <FunnelItem v={fmtInt(f.rejected)} l='Rejected' />
          <FunnelItem v={fmtInt(f.pending)} l='Pending' />
          <FunnelItem v={fmtInt(f.expired)} l='Expired' />
          <FunnelItem v={fmtPct(f.acceptanceRate)} l='Accept rate' />
          <FunnelItem v={fmtPct(f.rejectionRate)} l='Reject rate' alert={rejectionAlert} />
          <FunnelItem v={fmtInt(f.acceptedNotLaunched)} l='Accepted, not launched' alert={anlAlert} />
          <FunnelItem v={fmtInt(f.blacklisted)} l='Blacklisted' />
        </div>
      </div>

      {/* Meta assets & settings */}
      {detail.settings && (
        <div style={{ marginBottom: 14 }}>
          <div className='sl-card-label'>Meta assets & settings</div>
          <div className='sl-funnel' style={{ marginTop: 6 }}>
            <FunnelItem v={detail.settings.config.targetRoas != null ? `${detail.settings.config.targetRoas}x` : '—'} l='Target ROAS' />
            <FunnelItem v={detail.settings.config.pauseGuideRoas != null ? `${detail.settings.config.pauseGuideRoas}x` : '—'} l='Pause guide' />
            <FunnelItem v={detail.settings.config.enableAutoPause ? 'On' : 'Off'} l='Auto-pause' />
            <FunnelItem v={detail.settings.config.enableCreatorPausedAlerts ? 'On' : 'Off'} l='Creator-paused alerts' />
            <FunnelItem v={detail.settings.config.adsSpendLimit != null ? fmtMoney(detail.settings.config.adsSpendLimit) : '—'} l='Spend limit' />
            <FunnelItem v={detail.settings.config.budgetRampNumber} l='Budget ramp' />
            <FunnelItem v={`${detail.settings.config.recommendationWindowDays}d`} l='Rec. window' />
            <FunnelItem v={detail.settings.config.billingCadence || '—'} l='Billing cadence' />
            <FunnelItem v={detail.settings.config.spotlightPlatformFeePercentage != null ? `${detail.settings.config.spotlightPlatformFeePercentage}%` : '—'} l='Platform fee' />
            <FunnelItem v={detail.settings.config.hasPaymentMethod ? 'Yes' : 'No'} l='Payment method' />
          </div>
          <div className='sl-meta-kv' style={{ marginTop: 10 }}>
            <MetaKV label='Meta pixel' value={detail.settings.meta.metaPixelId} href={detail.settings.meta.metaPixelUrl} />
            <MetaKV label='Dataset' value={detail.settings.meta.experimentDataset} href={detail.settings.meta.experimentDatasetUrl} />
            <MetaKV label='Ad set' value={detail.settings.meta.metaExperimentAdSetId} href={detail.settings.meta.metaExperimentAdSetUrl} />
            <MetaKV label='iOS14 campaign' value={detail.settings.meta.ios14CampaignId} href={detail.settings.meta.ios14CampaignUrl} />
            <MetaKV label='Campaign' value={detail.settings.meta.campaignName} />
            <MetaKV label='Objective' value={detail.settings.meta.campaignObjective} />
            {detail.settings.config.utmParams && <MetaKV label='UTM' value={detail.settings.config.utmParams} />}
            {detail.stripeCustomerUrl && <MetaKV label='Stripe customer' value='Open in Stripe' href={detail.stripeCustomerUrl} />}
          </div>
        </div>
      )}

      {/* Spotlights — nested subtable, styled like SpotlightTableRow */}
      <SpotlightsSubTable
        spotlights={detail.spotlights}
        identityLabel='Creator'
        getIdentity={s => (s.creatorUsername ? `@${s.creatorUsername}` : s.creatorName || s.User_id)}
        getActions={s =>
          [
            s.metaAdUrl && { label: 'View ad on Meta', icon: faExternalLinkAlt, onClick: () => window.open(s.metaAdUrl, '_blank', 'noopener') },
            {
              label: 'Copy Spotlight ID',
              icon: faCopy,
              onClick: () => {
                navigator.clipboard?.writeText(String(s.id));
                cogoToast.success('Copied Spotlight ID');
              }
            }
          ].filter(Boolean)
        }
        extraColumns={[
          { key: 'cpa', label: 'CPA', num: true, render: s => (s.cpa ? fmtMoney(s.cpa) : '—') },
          { key: 'orders', label: 'Orders', num: true, render: s => fmtInt(s.orders) },
          {
            key: 'flags',
            label: 'Flags',
            render: s => (
              <>
                {!!s.pauseRecommended && (
                  <span className='sl-pill red' title='Recommended to pause'>
                    ↓
                  </span>
                )}
                {s.blockedReason ? (
                  <span className='sl-pill amber' title={s.blockedReason}>
                    blocked
                  </span>
                ) : null}
              </>
            )
          }
        ]}
      />
    </div>
  );
};

const FunnelItem = ({ v, l, alert }) => (
  <div className='sl-funnel-item' style={alert ? { borderColor: '#da0e3f' } : undefined}>
    <div className='v' style={alert ? { color: '#da0e3f' } : undefined}>
      {v}
    </div>
    <div className='l'>{l}</div>
  </div>
);

const MetaKV = ({ label, value, href }) => (
  <div className='sl-kv'>
    <span className='k'>{label}</span>
    {value && href ? (
      <a className='v sl-kv-link' href={href} target='_blank' rel='noreferrer' onClick={e => e.stopPropagation()}>
        {value} ↗
      </a>
    ) : (
      <span className='v'>{value || '—'}</span>
    )}
  </div>
);

export default SpotlightsBrands;
